/**
 * @file CarrierWebhookPanel.tsx
 * @description Подписка на статусы доставки — регистрация и отписка
 * (phase19.13 шаг 7).
 *
 * Без подписки статусы приходят только опросом: диагностика горит жёлтым, а
 * менеджер узнаёт о вручении с задержкой. Адрес приёмника задаётся окружением,
 * поэтому в UI только две кнопки и текущее состояние — вводить URL руками
 * нельзя (опечатка = «подписка есть, событий нет»).
 *
 * Отписка — через подтверждение: это откат к опросу, а не безобидное действие.
 */

import { AlertTriangle, BellOff, BellRing, CheckCircle2, Loader2 } from "lucide-react";
import { useState } from "react";

import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import { Alert } from "../ui/alert";
import { Button } from "../ui/button";
import { useNotify } from "../ui/NotifyProvider";
import { useHasPermission } from "../../auth/hooks";
import { ApiError } from "../../lib/api-client";
import {
  CARRIER_WEBHOOK_ERROR_TEXT,
  registerCarrierWebhook,
  unregisterCarrierWebhook,
  type CarrierKind,
  type CarrierWebhookResult,
} from "../../lib/api-shop-carriers";

interface Props {
  carrier: CarrierKind;
  /** Текущий uuid подписки из конфига ТК; null — подписки нет. */
  subscriptionUuid: string | null;
  /** ТК не подключена к магазину — подписывать нечего. */
  disabled?: boolean;
  /** Подписка изменилась: перечитать конфиг и перезапустить диагностику. */
  onChanged: () => void | Promise<void>;
}

/** Ошибка сервера → человеческий текст (реестр кодов + запасной message). */
function webhookErrorText(err: unknown): string {
  if (!(err instanceof ApiError)) return "Не удалось изменить подписку";
  return CARRIER_WEBHOOK_ERROR_TEXT[err.code] ?? err.message;
}

/** Тост по ответу сервера: текст даёт сервер, mock помечаем отдельно. */
function outcomeText(result: CarrierWebhookResult): string {
  return result.mock ? `${result.message} (тестовый режим)` : result.message;
}

export function CarrierWebhookPanel({
  carrier,
  subscriptionUuid,
  disabled = false,
  onChanged,
}: Props) {
  const notify = useNotify();
  const canManage = useHasPermission("SHOP_MANAGE_CARRIERS");
  const [busy, setBusy] = useState<"register" | "unregister" | null>(null);
  const [confirmOff, setConfirmOff] = useState(false);

  async function run(
    kind: "register" | "unregister",
    action: () => Promise<CarrierWebhookResult>,
  ): Promise<void> {
    setBusy(kind);
    try {
      const result = await action();
      notify.success(outcomeText(result));
      // Состояние подписки живёт на сервере — перечитываем конфиг и
      // диагностику, а не правим индикатор руками.
      await onChanged();
    } catch (err) {
      notify.error(webhookErrorText(err));
    } finally {
      setBusy(null);
      setConfirmOff(false);
    }
  }

  return (
    <div className="flex flex-col gap-3">
      <div>
        <div className="text-sm font-semibold">Подписка на статусы доставки</div>
        <p className="text-xs text-on-surface-variant">
          Пока подписки нет, статусы приезжают только автоопросом — вручение
          менеджер увидит с задержкой.
        </p>
      </div>

      {disabled ? (
        <Alert severity="info">
          Сначала подключите перевозчика к магазину и сохраните настройки.
        </Alert>
      ) : (
        <div
          className={`flex items-start gap-2 rounded-md border p-3 text-sm ${
            subscriptionUuid ? "" : "border-dashed"
          }`}
        >
          {subscriptionUuid ? (
            <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-emerald-600" />
          ) : (
            <AlertTriangle className="mt-0.5 size-4 shrink-0 text-amber-600" />
          )}
          <div className="min-w-0">
            <div className="font-medium">
              {subscriptionUuid ? "Подписка оформлена" : "Подписка не оформлена"}
            </div>
            {subscriptionUuid ? (
              <div className="truncate font-mono text-[11px] text-on-surface-variant">
                {subscriptionUuid}
              </div>
            ) : null}
          </div>
        </div>
      )}

      {canManage ? (
        <div className="flex flex-wrap gap-2">
          <Button
            className="min-h-12"
            onClick={() => void run("register", () => registerCarrierWebhook(carrier))}
            disabled={disabled || busy !== null}
          >
            {busy === "register" ? (
              <Loader2 className="mr-2 size-4 animate-spin" />
            ) : (
              <BellRing className="mr-2 size-4" />
            )}
            Зарегистрировать webhook
          </Button>
          {subscriptionUuid ? (
            <Button
              variant="destructive"
              className="min-h-12"
              onClick={() => setConfirmOff(true)}
              disabled={disabled || busy !== null}
            >
              <BellOff className="mr-2 size-4" />
              Отписаться
            </Button>
          ) : null}
        </div>
      ) : (
        <p className="text-xs text-on-surface-variant">
          Менять подписку может администратор магазина с правом на перевозчиков.
        </p>
      )}

      <AlertDialog open={confirmOff} onOpenChange={(v) => (!v ? setConfirmOff(false) : null)}>
        <AlertDialogContent fullScreen className="max-w-md gap-3">
          <AlertDialogHeader>
            <AlertDialogTitle>Снять подписку на статусы?</AlertDialogTitle>
            <AlertDialogDescription>
              Перевозчик перестанет присылать события. Статусы будут обновляться
              только автоопросом — вручение и проблемы доставки менеджер увидит
              позже, чем сейчас.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <Button
              variant="ghost"
              className="min-h-12"
              onClick={() => setConfirmOff(false)}
              disabled={busy !== null}
            >
              Отмена
            </Button>
            <Button
              variant="destructive"
              className="min-h-12"
              onClick={() =>
                void run("unregister", () => unregisterCarrierWebhook(carrier))
              }
              disabled={busy !== null}
            >
              {busy === "unregister" ? (
                <Loader2 className="mr-2 size-4 animate-spin" />
              ) : null}
              Снять подписку
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
